import { Fragment, useRef } from "react"
import { Kbd } from "@/design-system/index.ts"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/primitives/dialog.tsx"
import { keysOf } from "@/shortcuts/keys.ts"
import { shortcutSections } from "@/shortcuts/registry.ts"

/*
 * Every keyboard shortcut the Member has, opened with `?`, from the sidebar's help button or from
 * the account menu. It reads the same registry the listener does, so it can't list a key that
 * does nothing, or miss one that does. A Member who isn't an Admin isn't shown the Admin pages'
 * chords.
 */
export function ShortcutsDialog({
  open,
  onOpenChange,
  admin,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  admin: boolean
}) {
  // The dialog is read, not filled in, so focus rests on it rather than lighting up its close
  // button; Tab still reaches the button, and Esc closes it.
  const popup = useRef<HTMLDivElement>(null)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={popup} initialFocus={popup} size="md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>Page shortcuts work wherever you aren't typing.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4 pb-5">
          {shortcutSections({ admin }).map(({ scope, heading, shortcuts }) => (
            <section key={scope} aria-label={heading}>
              <h3 className="py-1.5 text-xs font-medium text-subtle-foreground">{heading}</h3>
              <ul>
                {shortcuts.map(({ id, label, keys: [, ...others] }) => (
                  <li key={id} className="flex h-8 items-center gap-4">
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <Kbd shortcut={id} always />
                      {/* A shortcut's other keys: j moves down, and so does ↓. */}
                      {others.map((keys, index) => (
                        <Fragment key={index}>
                          <span className="text-2xs text-subtle-foreground">or</span>
                          <Kbd keys={keysOf(keys)[0] ?? []} always />
                        </Fragment>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
